/**
 * AG Custom Header
 * Drawer, Learn More panel, scroll hide/show, cart drawer + count.
 */
(function () {
  'use strict';

  var LEARN_MORE_OPEN_GRACE_MS = 450;
  var LEARN_MORE_SCROLL_CLOSE_THRESHOLD = 8;
  var BAR_REVEAL_SUPPRESS_MS = 500;

  var SELECTORS = {
    root: '[data-agh-root]',
    shell: '.agh__scroll-shell',
    bar: '.agh__bar',
    learnMore: '.agh__learn-more',
    learnMoreOverlay: '.agh__learn-more-overlay',
    learnMoreTrigger: '.agh__learn-more-trigger',
    drawer: '.agh__drawer',
    cartCount: '.agh__cart-count',
    section: '.shopify-section--ag-custom-header'
  };

  var AgCustomHeader = function (root) {
    this.root = root;
    this.section = root.closest(SELECTORS.section);
    this.shell = root.querySelector(SELECTORS.shell);
    this.bar = root.querySelector(SELECTORS.bar);
    this.learnMore = root.querySelector(SELECTORS.learnMore);
    this.learnMoreOverlay = root.querySelector(SELECTORS.learnMoreOverlay);
    this.learnMoreTriggers = root.querySelectorAll(SELECTORS.learnMoreTrigger);
    this.drawer = root.querySelector(SELECTORS.drawer);
    this.cartCount = root.querySelector(SELECTORS.cartCount);
    this.menuToggle = root.querySelector('[data-action="open-drawer"]');

    this.behavior = root.dataset.headerBehavior || 'sticky';
    this.scrollThreshold = parseInt(root.dataset.scrollThreshold, 10) || 100;
    this.scrollSensitivity = parseInt(root.dataset.scrollSensitivity, 10) || 30;

    this.state = {
      learnMoreOpen: false,
      learnMoreOpenedAt: 0,
      learnMoreOpenScrollY: 0,
      drawerOpen: false,
      barHidden: false,
      lastScrollY: window.scrollY,
      accumulatedDown: 0,
      accumulatedUp: 0,
      suppressBarRevealUntil: 0
    };

    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.handleScrollFrame = this.handleScrollFrame.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);

    this.init();
  };

  /*
    Positioning is pure CSS: the section carries `position: sticky; top: 0` from the
    inline style in sections/ag-custom-header.liquid (and `position: relative` when the
    behavior is static). There is deliberately no JS fixed-pin here any more.

    The old polyfill re-wrote `section.style.top` from a measured rect on every scroll
    frame. `top` is a layout property, so those writes ran on the main thread while the
    page itself scrolled on the compositor — the header always landed a frame or two
    behind, which showed up as wobble against the announcement bar on high-refresh
    (120Hz+) screens. It was only visible in the first fold because that is the only
    stretch where the value actually changes; once clamped to 0 it stopped drifting.

    Its stated reason ("body overflow-x:hidden kills position:sticky") is obsolete:
    html/body now use `overflow-x: clip`, which does not create a scroll container, so
    native sticky works. See the note in assets/custom.css.
  */
  AgCustomHeader.prototype.init = function () {
    this.bindEvents();
    this.setHeaderHeight();
    this.updateCartCountFromDom();

    var self = this;
    // Announcement / Replo reorder can change layout a frame or two in
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        self.setHeaderHeight();
      });
    });
  };

  AgCustomHeader.prototype.bindEvents = function () {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick.bind(this));
    this.root.addEventListener('click', this.onClick);

    document.documentElement.addEventListener('cart:refresh', this.handleCartEvent.bind(this));
    document.addEventListener('cart:change', this.handleCartEvent.bind(this));
    document.addEventListener('cart:changed', this.handleCartEvent.bind(this));
    document.addEventListener('drawer:close', this.handleCartDrawerClose.bind(this));

    this.bindCartDrawerObserver();
  };

  AgCustomHeader.prototype.bindCartDrawerObserver = function () {
    var self = this;
    var cartDrawer = document.getElementById('mini-cart') || document.querySelector('cart-drawer');
    if (!cartDrawer || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'open') continue;
        if (cartDrawer.hasAttribute('open') || cartDrawer.open) continue;
        self.handleCartDrawerClose();
      }
    });

    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
  };

  AgCustomHeader.prototype.lockBodyScroll = function () {
    document.body.style.overflow = 'hidden';
  };

  AgCustomHeader.prototype.restoreBodyScroll = function () {
    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.body.classList.remove('drawer-open', 'no-scroll', 'overflow-hidden');
  };

  AgCustomHeader.prototype.handleCartDrawerClose = function () {
    var self = this;
    this.restoreBodyScroll();

    // Re-measure after the body overflow unlock settles
    window.requestAnimationFrame(function () {
      self.setHeaderHeight();
    });
  };

  AgCustomHeader.prototype.onScroll = function () {
    /* FAQ accordion height changes nudge scrollY on iOS — ignore briefly */
    if (window.__AG_SCROLL_SUPPRESS_UNTIL__ && Date.now() < window.__AG_SCROLL_SUPPRESS_UNTIL__) {
      this.state.lastScrollY = window.scrollY;
      this.ticking = false;
      return;
    }
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.handleScrollFrame);
  };

  AgCustomHeader.prototype.onDocumentClick = function (event) {
    if (!this.state.learnMoreOpen) return;
    if (this.root.contains(event.target)) return;
    this.closeLearnMore();
  };

  AgCustomHeader.prototype.onClick = function (event) {
    var actionEl = event.target.closest('[data-action]');
    if (!actionEl || !this.root.contains(actionEl)) return;

    var action = actionEl.getAttribute('data-action');

    if (action === 'toggle-learn-more') {
      event.preventDefault();
      this.toggleLearnMore();
      return;
    }

    if (action === 'close-learn-more') {
      event.preventDefault();
      this.closeLearnMore();
      return;
    }

    if (action === 'open-drawer') {
      event.preventDefault();
      this.openDrawer();
      return;
    }

    if (action === 'close-drawer') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (action === 'open-cart') {
      event.preventDefault();
      this.openCartDrawer();
      return;
    }

    if (this.state.drawerOpen && event.target.closest('.agh__drawer-panel a[href]')) {
      this.closeDrawer();
    }
  };

  AgCustomHeader.prototype.openCartDrawer = function () {
    var miniCart = document.getElementById('mini-cart');
    if (miniCart) {
      miniCart.open = true;
      this.lockBodyScroll();
      document.dispatchEvent(
        new CustomEvent('cart:open', {
          bubbles: true,
          detail: { source: 'ag-custom-header' }
        })
      );
      return;
    }

    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      this.lockBodyScroll();
      return;
    }

    window.location.href = '/cart';
  };

  AgCustomHeader.prototype.onKeydown = function (event) {
    if (event.key !== 'Escape') return;

    if (this.state.drawerOpen) {
      this.closeDrawer();
      if (this.menuToggle) this.menuToggle.focus();
      return;
    }

    var openCart = document.querySelector('cart-drawer[open], #mini-cart[open]');
    if (openCart) {
      if ('open' in openCart) {
        openCart.open = false;
      } else {
        openCart.removeAttribute('open');
      }
      this.handleCartDrawerClose();
      return;
    }

    if (this.state.learnMoreOpen) {
      this.closeLearnMore();
      var trigger = this.learnMoreTriggers[0];
      if (trigger) trigger.focus();
    }
  };

  AgCustomHeader.prototype.onResize = function () {
    this.setHeaderHeight();
    this.updateLearnMoreTopPosition();
  };

  AgCustomHeader.prototype.handleScrollFrame = function () {
    this.ticking = false;
    var currentY = window.scrollY;
    var delta = currentY - this.state.lastScrollY;
    var closedLearnMoreFromScroll = false;

    if (this.state.learnMoreOpen) {
      var graceElapsed = Date.now() - this.state.learnMoreOpenedAt;
      var frameDelta = Math.abs(delta);
      var totalDelta = Math.abs(currentY - this.state.learnMoreOpenScrollY);

      if (
        graceElapsed > LEARN_MORE_OPEN_GRACE_MS &&
        (frameDelta >= LEARN_MORE_SCROLL_CLOSE_THRESHOLD || totalDelta >= LEARN_MORE_SCROLL_CLOSE_THRESHOLD)
      ) {
        this.closeLearnMore({ fromScroll: true, scrollingDown: delta > 0 });
        closedLearnMoreFromScroll = true;
      } else if (this.behavior === 'sticky_scroll_hide' && delta > 0 && currentY > this.scrollThreshold) {
        this.state.accumulatedDown += delta;
      }
    }

    if (this.state.learnMoreOpen && this.root.classList.contains('agh--learn-more-at-top')) {
      this.updateLearnMoreTopPosition();
    }

    if (this.behavior === 'sticky_scroll_hide' && !this.state.learnMoreOpen) {
      if (currentY <= this.scrollThreshold) {
        this.showBar();
        this.state.accumulatedDown = 0;
        this.state.accumulatedUp = 0;
      } else if (delta > 0) {
        this.state.accumulatedDown += delta;
        this.state.accumulatedUp = 0;

        if (!this.state.barHidden && this.state.accumulatedDown >= this.scrollSensitivity) {
          this.hideBar();
          this.state.accumulatedDown = 0;
        }
      } else if (delta < 0 && !closedLearnMoreFromScroll) {
        this.state.accumulatedUp += Math.abs(delta);
        this.state.accumulatedDown = 0;

        if (this.state.barHidden && this.state.accumulatedUp >= this.scrollSensitivity) {
          if (Date.now() >= this.state.suppressBarRevealUntil) {
            this.showBar();
          }
          this.state.accumulatedUp = 0;
        }
      }
    }

    this.state.lastScrollY = currentY;
  };

  AgCustomHeader.prototype.hideBar = function () {
    if (this.state.barHidden || this.behavior !== 'sticky_scroll_hide') return;
    this.root.classList.add('is-bar-hidden');
    this.state.barHidden = true;
  };

  AgCustomHeader.prototype.showBar = function () {
    if (!this.state.barHidden) return;
    this.root.classList.remove('is-bar-hidden');
    this.state.barHidden = false;
  };

  AgCustomHeader.prototype.toggleLearnMore = function () {
    if (this.state.learnMoreOpen) {
      this.closeLearnMore();
    } else {
      this.openLearnMore();
    }
  };

  AgCustomHeader.prototype.updateLearnMoreTopPosition = function () {
    if (!this.root.classList.contains('agh--learn-more-at-top')) return;
    var aaghor = this.shell || this.bar;
    if (!aaghor) return;
    this.root.style.setProperty('--agh-learn-more-top', aaghor.getBoundingClientRect().bottom + 'px');
  };

  AgCustomHeader.prototype.openLearnMore = function () {
    if (!this.learnMore) return;

    this.showBar();
    this.state.learnMoreOpenScrollY = window.scrollY;
    this.state.learnMoreOpenedAt = Date.now();
    this.state.lastScrollY = window.scrollY;
    this.state.accumulatedDown = 0;
    this.state.accumulatedUp = 0;
    this.state.suppressBarRevealUntil = 0;

    var atTop = window.scrollY <= this.scrollThreshold;

    if (atTop) {
      this.root.classList.add('agh--learn-more-at-top');
    } else {
      this.root.classList.remove('agh--learn-more-at-top');
      this.root.style.removeProperty('--agh-learn-more-top');
    }

    this.learnMore.hidden = false;
    this.learnMore.setAttribute('aria-hidden', 'false');
    this.state.learnMoreOpen = true;
    this.root.classList.add('is-learn-more-open');

    if (this.learnMoreOverlay) {
      this.learnMoreOverlay.hidden = false;
      this.learnMoreOverlay.setAttribute('aria-hidden', 'false');
    }

    var self = this;

    window.requestAnimationFrame(function () {
      self.updateLearnMoreTopPosition();
      self.learnMore.classList.add('is-open');
      self.setHeaderHeight();

      window.requestAnimationFrame(function () {
        self.updateLearnMoreTopPosition();
        self.setHeaderHeight();
      });
    });

    this.learnMoreTriggers.forEach(function (trigger) {
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    });
  };

  AgCustomHeader.prototype.closeLearnMore = function (options) {
    options = options || {};

    if (!this.learnMore || !this.state.learnMoreOpen) return;

    var overlayAtTop = this.root.classList.contains('agh--learn-more-at-top');
    var closeDelay = overlayAtTop ? 250 : 350;

    this.learnMore.classList.remove('is-open');
    this.learnMore.setAttribute('aria-hidden', 'true');
    this.state.learnMoreOpen = false;
    this.root.classList.remove('is-learn-more-open');

    if (this.learnMoreOverlay) {
      this.learnMoreOverlay.setAttribute('aria-hidden', 'true');
    }

    this.learnMoreTriggers.forEach(function (trigger) {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (options.fromScroll && this.behavior === 'sticky_scroll_hide') {
      if (window.scrollY > this.scrollThreshold) {
        this.hideBar();
      }

      this.state.suppressBarRevealUntil = Date.now() + BAR_REVEAL_SUPPRESS_MS;
      this.state.accumulatedUp = 0;
      this.state.accumulatedDown = 0;
      this.state.lastScrollY = window.scrollY;
    }

    var learnMore = this.learnMore;
    var learnMoreOverlay = this.learnMoreOverlay;
    var self = this;

    window.setTimeout(function () {
      if (!learnMore.classList.contains('is-open')) {
        learnMore.hidden = true;

        if (overlayAtTop) {
          self.root.classList.remove('agh--learn-more-at-top');
          self.root.style.removeProperty('--agh-learn-more-top');
        }
      }

      if (learnMoreOverlay && !learnMore.classList.contains('is-open')) {
        learnMoreOverlay.hidden = true;
      }

      self.setHeaderHeight();
      if (options.fromScroll) {
        self.state.lastScrollY = window.scrollY;
      }
    }, closeDelay);

    this.setHeaderHeight();
  };

  AgCustomHeader.prototype.openDrawer = function () {
    if (!this.drawer) return;

    this.drawer.hidden = false;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.state.drawerOpen = true;
    document.body.style.overflow = 'hidden';

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'true');
    }
  };

  AgCustomHeader.prototype.closeDrawer = function () {
    if (!this.drawer || !this.state.drawerOpen) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.state.drawerOpen = false;
    document.body.style.overflow = '';

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'false');
    }

    var drawer = this.drawer;
    window.setTimeout(function () {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
    }, 350);
  };

  AgCustomHeader.prototype.setHeaderHeight = function () {
    if (!this.section || !this.bar) return;

    var barHeight = this.bar.offsetHeight;
    var overlayAtTop = this.root.classList.contains('agh--learn-more-at-top');
    var headerHeight = barHeight;

    if (this.state.learnMoreOpen && !overlayAtTop) {
      headerHeight = this.root.offsetHeight;
    }

    document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
    document.documentElement.style.setProperty('--header-height-without-bottom-nav', barHeight + 'px');
    this.root.style.setProperty('--agh-bar-height', barHeight + 'px');
  };

  AgCustomHeader.prototype.handleCartEvent = function (event) {
    if (event.detail && event.detail.cart && typeof event.detail.cart.item_count === 'number') {
      this.updateCartCount(event.detail.cart.item_count);
      return;
    }

    if (event.detail && typeof event.detail.item_count === 'number') {
      this.updateCartCount(event.detail.item_count);
      return;
    }

    var self = this;
    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        self.updateCartCount(cart.item_count || 0);
      })
      .catch(function () {});
  };

  AgCustomHeader.prototype.updateCartCountFromDom = function () {
    if (!this.cartCount) return;
    var count = parseInt(this.cartCount.textContent, 10) || 0;
    this.updateCartCount(count);
  };

  AgCustomHeader.prototype.updateCartCount = function (count) {
    if (!this.cartCount) return;
    this.cartCount.textContent = count;
    this.cartCount.classList.toggle('hidden', count <= 0);
  };

  function initAll() {
    document.querySelectorAll(SELECTORS.root).forEach(function (root) {
      if (root.__aghInstance) return;
      root.__aghInstance = new AgCustomHeader(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(SELECTORS.root);
    if (root && !root.__aghInstance) {
      root.__aghInstance = new AgCustomHeader(root);
    }
  });
})();
